import { Check, Crown, Lock, Pencil, Plus, Sparkles, Star, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import type { AdminFamily, AdminFamilyPlan, Addon, Child, FamilyPlan, FeatureInfo, FeatureKey, Plan } from '../lib/types';
import { Sheet } from '../ui/Sheet';
import { useParent } from './ParentContext';

/** Subscription plans (#99), phase 1: what families see, and what admins configure. */

/** Whether a child's family plan includes a feature. Older servers send no list: everything is included. */
export const planHas = (child: Pick<Child, 'features'> | undefined, f: string) => !child?.features || child.features.includes(f as FeatureKey);
/** Whether the parent hid a feature for this child (#454). */
export const childHides = (child: Pick<Child, 'hiddenFeatures' | 'offFeatures'> | undefined, f: string) => !!child?.hiddenFeatures?.includes(f) || !!child?.offFeatures?.includes(f);
/** Your level for an area of this child (#471): the family owner and older servers are Full. */
const myLevel = (child: Pick<Child, 'myAccess'> | undefined, area: string): 'none' | 'view' | 'edit' | 'full' => child?.myAccess?.[area] ?? 'full';
/** Whether you may at least do this much in an area for this child (#471). */
export const canDo = (child: Pick<Child, 'myAccess'> | undefined, area: string, need: 'view' | 'edit' | 'full') =>
  ['none', 'view', 'edit', 'full'].indexOf(myLevel(child, area)) >= ['none', 'view', 'edit', 'full'].indexOf(need);
/** Whether an admin turned a feature off for the whole server (#469). */
export const serverOff = (child: Pick<Child, 'offFeatures'> | undefined, f: string) => !!child?.offFeatures?.includes(f);
/** Whether a child has a feature to use: in the family plan, and not hidden by the parent (#454). */
export const childHas = (child: Pick<Child, 'features' | 'hiddenFeatures' | 'offFeatures'> | undefined, f: FeatureKey) => planHas(child, f) && !childHides(child, f);

const money = (cents: number) => (cents % 100 ? `$${(cents / 100).toFixed(2)}` : `$${cents / 100}`);
const shortDate = (ymd: string) => (ymd ? new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const limitText = (n: number | null, what: string) => (n === null ? `Unlimited ${what}` : `${n} ${what}`);

/** What a family's plan status means, in words. */
function statusText(p: { status: string; freeUntil: string }, plan?: Pick<Plan, 'priceMonthlyCents' | 'yearlyDiscountPct' | 'priceYearlyCents' | 'ambassadorAccess'> | null) {
  switch (p.status) {
    case 'unrestricted':
      return 'Everything is included';
    case 'free-for-life':
      // An ambassador's own plan (#895) is part of the program, not free for life.
      if (plan?.ambassadorAccess)
        return p.freeUntil ? `No charge until ${shortDate(p.freeUntil)}, then choose a plan to keep going` : 'No charge while you’re a Brand Ambassador';
      return 'Free for life';
    case 'trial':
      return `Free trial until ${shortDate(p.freeUntil)}`;
    case 'free':
      return p.freeUntil ? `Free until ${shortDate(p.freeUntil)}` : 'Free';
    default:
      return plan ? priceText(plan) : 'Paid plan';
  }
}

function priceText(p: Pick<Plan, 'priceMonthlyCents' | 'yearlyDiscountPct' | 'priceYearlyCents'>) {
  if (!p.priceMonthlyCents) return 'Free';
  // An exact yearly price wins over the % discount.
  const yearly = p.priceYearlyCents || Math.round(p.priceMonthlyCents * 12 * (1 - p.yearlyDiscountPct / 100));
  const saves = Math.round((1 - yearly / (p.priceMonthlyCents * 12)) * 100);
  return `${money(p.priceMonthlyCents)}/month${saves > 0 ? ` or ${money(yearly)}/year (save ${saves}%)` : ''}`;
}

/** What a feature add-on unlocks; older servers send only one. */
const addonFeatures = (a: Pick<Addon, 'feature' | 'features'>): FeatureKey[] => a.features ?? (a.feature ? [a.feature] : []);
/** Whether an add-on can be used with a plan (#394): it lists no plans, or lists this one. */
const addonOnPlan = (a: Pick<Addon, 'planIds'>, planId: string) => !a.planIds?.length || a.planIds.includes(planId);

const toggle = <T,>(list: T[], v: T, on: boolean) => (on ? [...list, v] : list.filter((x) => x !== v));

/** Shown in place of a feature the family's plan doesn't include. */
export function PlanLocked({ label, what }: { label: string; what?: string }) {
  const { parent } = useParent();
  const planName = parent.plan?.plan?.name;
  return (
    <section className="card plan-locked stack">
      <h2 className="with-icon">
        <Lock size={18} /> {label} isn’t in your plan
      </h2>
      <p className="muted">
        {what ?? `It isn’t included in ${planName ? `the ${planName} plan` : 'your plan'}.`}{' '}
        {parent.billing?.enabled ? (
          <>
            To add it, change your plan in <a href="/settings">Settings → Plan &amp; billing</a>.
          </>
        ) : (
          <>
            To add it, send us a note from <a href="/feedback">Feedback</a> and we’ll help you upgrade.
          </>
        )}
      </p>
    </section>
  );
}

/** The family's plan, in Settings → Plan & billing (#542); `children` (billing) goes at the bottom of the same card. */
export function YourPlan({ children }: { children?: ReactNode }) {
  const { parent } = useParent();
  const p: FamilyPlan | undefined = parent.plan;
  if (!p || p.status === 'unrestricted') return null;
  const used = (n: number, max: number | null) => (max === null ? `${n} (unlimited)` : `${n} of ${max}`);
  return (
    <section id="plan" className="card stack your-plan">
      <h2 className="with-icon">
        <Crown size={18} /> Your plan: {p.plan?.name}
      </h2>
      <p className="your-plan-status">{statusText(p, p.plan)}</p>
      {p.plan?.description && <p className="muted">{p.plan.description}</p>}
      <div className="plan-usage">
        <span>
          <strong>Adults</strong> {used(p.counts.adults, p.maxAdults)}
          {p.counts.invites ? ` · ${p.counts.invites} invited` : ''}
        </span>
        <span>
          <strong>Children</strong> {used(p.counts.children, p.maxChildren)}
        </span>
      </div>
      <ul className="plan-features">
        {p.catalog.map((f) => {
          const on = p.features.includes(f.key);
          return (
            <li key={f.key} className={on ? 'on' : 'off'} title={f.hint}>
              {on ? <Check size={14} /> : <Lock size={13} />} {f.label}
            </li>
          );
        })}
      </ul>
      {p.addons.length > 0 && (
        <p className="muted small">
          Add-ons: {p.addons.map((a) => `${a.name}${a.quantity > 1 ? ` ×${a.quantity}` : ''}${a.fromPlan ? ' (included)' : ''}`).join(', ')}
        </p>
      )}
      {p.plan?.earlyAccess && (
        <p className="small with-icon">
          <Sparkles size={14} /> You get early access to new features before everyone else.
        </p>
      )}
      {children}
    </section>
  );
}

// --- Admin -------------------------------------------------------------------------------------------------

interface PlansData {
  features: FeatureInfo[];
  earlyFeatures: FeatureKey[];
  plans: Plan[];
  addons: Addon[];
  unassignedFamilies: number;
}

/** Admin → Plans: subscription plans, add-ons and early access (#99). */
export function AdminPlans() {
  const { toast } = useParent();
  const [data, setData] = useState<PlansData | null>(null);
  const [editing, setEditing] = useState<Plan | 'new' | null>(null);
  const [editingAddon, setEditingAddon] = useState<Addon | 'new' | null>(null);
  const load = useCallback(() => {
    api.get<PlansData>('/api/admin/plans').then(setData).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);
  if (!data) return <p className="muted">Loading…</p>;
  const label = (k: string) => data.features.find((f) => f.key === k)?.label ?? k;

  async function saveEarly(next: FeatureKey[]) {
    try {
      const r = await api.put<{ earlyFeatures: FeatureKey[] }>('/api/admin/early-features', { features: next });
      setData((d) => d && { ...d, earlyFeatures: r.earlyFeatures });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <div className="stack admin-plans">
      <section className="card stack">
        <div className="row-between">
          <h2 className="with-icon">
            <Crown size={18} /> Plans
          </h2>
          <button className="btn primary small" onClick={() => setEditing('new')}>
            <Plus size={15} /> New plan
          </button>
        </div>
        <p className="muted small">
          Each plan sets the features a family gets and how many adults and children it can have. Families with no plan have everything
          ({data.unassignedFamilies} right now). Billing comes later: prices are saved for it.
        </p>
        {data.plans.length === 0 && <p className="muted">No plans yet. Create the Founders Circle EAP plan to start.</p>}
        <div className="plan-grid">
          {data.plans.map((p) => (
            <article key={p.id} className={`plan-card ${p.archived ? 'archived' : ''}`}>
              <div className="row-between">
                <strong>{p.name}</strong>
                <button className="icon-btn" onClick={() => setEditing(p)} aria-label={`Edit ${p.name}`}>
                  <Pencil size={15} />
                </button>
              </div>
              <span className="small">{priceText(p)}</span>
              <span className="muted small">
                {limitText(p.maxAdults, 'adults')} · {limitText(p.maxChildren, 'children')}
              </span>
              <div className="plan-tags">
                {p.isDefault && <span className="tag">Default for new families</span>}
                {p.recommended && <span className="tag">Recommended on the home page</span>}
                {p.ambassadorAccess ? <span className="tag">Ambassador access</span> : p.freeForLife && <span className="tag">Free for life</span>}
                {p.trialDays > 0 && <span className="tag">{p.trialDays}-day trial</span>}
                {p.freeUntil && <span className="tag">Free until {shortDate(p.freeUntil)}</span>}
                {p.earlyAccess && <span className="tag">Early access</span>}
                {(p.signupStart || p.signupEnd) && (
                  <span className="tag">
                    Sign-up {p.signupStart ? `from ${shortDate(p.signupStart)}` : ''} {p.signupEnd ? `until ${shortDate(p.signupEnd)}` : ''}
                  </span>
                )}
                {p.archived && <span className="tag">Archived</span>}
              </div>
              <span className="muted small">{p.features.length ? p.features.map(label).join(', ') : 'No extra features'}</span>
              <span className="muted small">
                {p.families ?? 0} famil{p.families === 1 ? 'y' : 'ies'}
              </span>
            </article>
          ))}
        </div>
      </section>

      <section className="card stack">
        <div className="row-between">
          <h2>Add-ons</h2>
          <button className="btn small" onClick={() => setEditingAddon('new')}>
            <Plus size={15} /> New add-on
          </button>
        </div>
        <p className="muted small">
          Extra adults, extra children or features, on top of a plan. Choose which plans each one is for, then include it in a plan or give it to one family.
        </p>
        {data.addons.length === 0 ? (
          <p className="muted small">No add-ons yet.</p>
        ) : (
          <table className="usage-kinds">
            <tbody>
              {data.addons.map((a) => (
                <tr key={a.id}>
                  <td>
                    {a.name} {a.archived && <span className="tag">Archived</span>}
                    <span className="muted small block">
                      {a.kind === 'feature' ? `Unlocks ${addonFeatures(a).map(label).join(', ')}` : `+${a.amount} ${a.kind}`}
                      {' · '}
                      {a.planIds?.length ? `For ${a.planIds.map((id) => data.plans.find((p) => p.id === id)?.name ?? '?').join(', ')}` : 'For every plan'}
                    </span>
                  </td>
                  <td className="num">{a.priceMonthlyCents ? `${money(a.priceMonthlyCents)}/month${a.priceYearlyCents ? ` · ${money(a.priceYearlyCents)}/year` : ''}` : 'Free'}</td>
                  <td className="num">
                    <button className="icon-btn" onClick={() => setEditingAddon(a)} aria-label={`Edit ${a.name}`}>
                      <Pencil size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <ScheduledPriceChanges data={data} />

      <section className="card stack">
        <h2 className="with-icon">
          <Sparkles size={18} /> Early access
        </h2>
        <p className="muted small">
          Features ticked here go only to plans with early access (like Founders Circle), even when other plans list them. Untick a feature to
          release it to every plan that includes it.
        </p>
        <div className="feature-checks">
          {data.features.map((f) => (
            <label key={f.key} className="check">
              <input
                type="checkbox"
                checked={data.earlyFeatures.includes(f.key)}
                onChange={(e) => saveEarly(e.target.checked ? [...data.earlyFeatures, f.key] : data.earlyFeatures.filter((x) => x !== f.key))}
              />
              <span>{f.label}</span>
            </label>
          ))}
        </div>
      </section>

      {editing && <PlanSheet plan={editing === 'new' ? null : editing} data={data} onClose={() => setEditing(null)} onSaved={load} />}
      {editingAddon && (
        <AddonSheet addon={editingAddon === 'new' ? null : editingAddon} features={data.features} plans={data.plans} onClose={() => setEditingAddon(null)} onSaved={load} />
      )}
    </div>
  );
}

/** Price changes for families already paying (#871): scheduled ones can be cancelled until they start. */
function ScheduledPriceChanges({ data }: { data: PlansData }) {
  const { toast } = useParent();
  type Change = { id: string; planName: string; monthlyCents: number; yearlyCents: number; startsAt: number; status: string; families: number; noticesSent: number };
  const [changes, setChanges] = useState<Change[]>([]);
  useEffect(() => {
    api.get<{ changes: Change[] }>('/api/admin/price-changes').then((r) => setChanges(r.changes), () => undefined);
  }, [data]);
  if (!changes.length) return null;
  async function cancel(c: Change) {
    if (!confirm(`Cancel the new ${c.planName} price for existing families? They keep paying what they pay now.`)) return;
    try {
      setChanges((await api.del<{ changes: Change[] }>(`/api/admin/price-changes/${c.id}`)).changes);
      toast({ text: `${c.planName}: price change cancelled`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return (
    <section className="card stack">
      <h2>Price changes for existing families</h2>
      <p className="muted small">Each family hears on its state’s day (Billing notices → Price change), then pays the new price from its first payment on or after the start date.</p>
      <div className="table-scroll">
        <table className="bn-table">
          <thead>
            <tr>
              <th>Plan</th>
              <th>New price</th>
              <th>Starts</th>
              <th>Families</th>
              <th>Notices</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {changes.map((c) => (
              <tr key={c.id}>
                <td>
                  <strong>{c.planName}</strong>
                </td>
                <td className="num">
                  {money(c.monthlyCents)}/month · {money(c.yearlyCents)}/year
                </td>
                <td>{day(c.startsAt)}</td>
                <td className="num">{c.families}</td>
                <td>{c.status === 'applied' ? 'Done: new price in place' : `${c.noticesSent} of ${c.families} sent`}</td>
                <td>
                  {c.status === 'scheduled' && (
                    <button type="button" className="btn small" onClick={() => cancel(c)}>
                      Cancel change
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const centsToText = (c: number) => (c ? (c / 100).toFixed(2) : '');
/** A time as the YYYY-MM-DD a date input takes, in this browser's time zone. */
const isoDay = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const textToCents = (t: string) => Math.max(0, Math.round(parseFloat(t || '0') * 100) || 0);
const limitToText = (n: number | null) => (n === null ? '' : String(n));
const textToLimit = (t: string) => (t.trim() === '' ? null : Math.max(0, parseInt(t, 10) || 0));

function PlanSheet({ plan, data, onClose, onSaved }: { plan: Plan | null; data: PlansData; onClose: () => void; onSaved: () => void }) {
  const { toast } = useParent();
  const [name, setName] = useState(plan?.name ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [price, setPrice] = useState(centsToText(plan?.priceMonthlyCents ?? 0));
  const [discount, setDiscount] = useState(String(plan?.yearlyDiscountPct ?? 0));
  const [yearly, setYearly] = useState(plan?.priceYearlyCents ? centsToText(plan.priceYearlyCents) : '');
  const [adults, setAdults] = useState(limitToText(plan ? plan.maxAdults : 2));
  const [children, setChildren] = useState(limitToText(plan ? plan.maxChildren : 3));
  const [features, setFeatures] = useState<FeatureKey[]>(plan?.features ?? []);
  const [addonIds, setAddonIds] = useState<string[]>(plan?.addonIds ?? []);
  const [earlyAccess, setEarlyAccess] = useState(!!plan?.earlyAccess);
  const [free, setFree] = useState<'none' | 'life' | 'ambassador' | 'trial' | 'dates'>(plan?.ambassadorAccess ? 'ambassador' : plan?.freeForLife ? 'life' : plan?.trialDays ? 'trial' : plan?.freeFrom || plan?.freeUntil ? 'dates' : 'none');
  const [trialDays, setTrialDays] = useState(String(plan?.trialDays || 60));
  const [freeFrom, setFreeFrom] = useState(plan?.freeFrom ?? '');
  const [freeUntil, setFreeUntil] = useState(plan?.freeUntil ?? '');
  const [signupStart, setSignupStart] = useState(plan?.signupStart ?? '');
  const [signupEnd, setSignupEnd] = useState(plan?.signupEnd ?? '');
  const [isDefault, setIsDefault] = useState(!!plan?.isDefault);
  const [recommended, setRecommended] = useState(!!plan?.recommended);
  const [archived, setArchived] = useState(!!plan?.archived);
  // The plan this is the bigger (Large Family) version of (#633).
  const [largerOf, setLargerOf] = useState(plan?.largerOf ?? '');
  const [busy, setBusy] = useState(false);
  // Families already paying (#871): when the price changes, keep their price or move them from a date.
  const [payInfo, setPayInfo] = useState<{ paying: number; oldPriceFamilies: number; earliest: number } | null>(null);
  const [moveExisting, setMoveExisting] = useState(true);
  const [startDate, setStartDate] = useState('');
  useEffect(() => {
    if (!plan) return;
    api
      .get<{ paying: number; oldPriceFamilies: number; earliest: number }>(`/api/admin/plans/${plan.id}/price-change`)
      .then((r) => {
        setPayInfo(r);
        setStartDate(isoDay(r.earliest));
      })
      .catch(() => undefined);
  }, [plan]);
  const priceChanged =
    !!plan &&
    (textToCents(price) !== plan.priceMonthlyCents ||
      (yearly.trim() ? textToCents(yearly) : 0) !== plan.priceYearlyCents ||
      (parseInt(discount, 10) || 0) !== plan.yearlyDiscountPct);
  // Paying families to ask about: everyone on the plan when the price is being changed, or those still on an old one.
  const askAbout = payInfo ? (priceChanged ? payInfo.paying : payInfo.oldPriceFamilies) : 0;

  async function save() {
    setBusy(true);
    const body = {
      name: name.trim(),
      description: description.trim(),
      priceMonthlyCents: textToCents(price),
      yearlyDiscountPct: Math.min(100, Math.max(0, parseInt(discount, 10) || 0)),
      priceYearlyCents: yearly.trim() ? textToCents(yearly) : 0,
      maxAdults: textToLimit(adults),
      maxChildren: textToLimit(children),
      features,
      addonIds,
      earlyAccess,
      freeForLife: free === 'life',
      ambassadorAccess: free === 'ambassador',
      trialDays: free === 'trial' ? Math.max(1, parseInt(trialDays, 10) || 0) : 0,
      freeFrom: free === 'dates' ? freeFrom : '',
      freeUntil: free === 'dates' ? freeUntil : '',
      signupStart,
      signupEnd,
      isDefault,
      recommended,
      archived,
      sort: plan?.sort ?? 0,
      largerOf,
    };
    try {
      if (plan) await api.put(`/api/admin/plans/${plan.id}`, body);
      else await api.post('/api/admin/plans', body);
      if (plan && askAbout > 0 && moveExisting) {
        await api.post(`/api/admin/plans/${plan.id}/price-change`, { startsAt: new Date(`${startDate}T12:00:00`).getTime() });
        toast({ text: `${body.name} saved. ${askAbout} famil${askAbout === 1 ? 'y moves' : 'ies move'} to the new price from ${new Date(`${startDate}T12:00:00`).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}.`, tone: 'good' });
      } else toast({ text: `${body.name} saved.`, tone: 'good' });
      onSaved();
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!plan || !confirm(`Delete the ${plan.name} plan?`)) return;
    try {
      await api.del(`/api/admin/plans/${plan.id}`);
      onSaved();
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <Sheet title={plan ? `Edit ${plan.name}` : 'New plan'} onClose={onClose}>
      <div className="stack plan-form">
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Founders Circle EAP" />
        </label>
        <label>
          Description <span className="muted small">(optional, families see it)</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} rows={2} />
        </label>
        <div className="field-row">
          <label>
            Price per month ($)
            <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0 = free" />
          </label>
          <label>
            Yearly discount (%)
            <input type="number" min={0} max={100} value={discount} onChange={(e) => setDiscount(e.target.value)} disabled={!!yearly.trim()} />
          </label>
          <label>
            Yearly price ($)
            <input inputMode="decimal" value={yearly} onChange={(e) => setYearly(e.target.value)} placeholder="blank = use discount" />
          </label>
        </div>
        {plan && payInfo && askAbout > 0 && (
          <div className="price-move">
            <strong>
              {askAbout} famil{askAbout === 1 ? 'y pays' : 'ies pay'} {priceChanged ? 'the current price' : 'an older price'}
            </strong>
            <p className="muted small">New families get the new price as soon as you save. Choose what happens to the families already paying:</p>
            <label className="check">
              <input type="radio" name="price-move" checked={!moveExisting} onChange={() => setMoveExisting(false)} />
              <span>
                <strong>Keep their price</strong>
                <span className="muted small block">They keep paying what they pay now until they change plans.</span>
              </span>
            </label>
            <label className="check">
              <input type="radio" name="price-move" checked={moveExisting} onChange={() => setMoveExisting(true)} />
              <span>
                <strong>Move them to the new price</strong>
                <span className="muted small block">
                  Each family gets the Price change notice on its state’s day, then pays the new price from its first payment on or after this date. Nothing is
                  charged early and there’s no proration.
                </span>
              </span>
            </label>
            {moveExisting && (
              <label>
                New price starts
                <input type="date" value={startDate} min={isoDay(payInfo.earliest)} onChange={(e) => setStartDate(e.target.value)} />
                <span className="muted small">
                  {new Date(payInfo.earliest).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })} at the earliest, so every family hears inside its state’s window (Billing notices → Price change).
                </span>
              </label>
            )}
          </div>
        )}
        <div className="field-row">
          <label>
            Adults <span className="muted small">(blank = unlimited)</span>
            <input type="number" min={0} value={adults} onChange={(e) => setAdults(e.target.value)} />
          </label>
          <label>
            Children <span className="muted small">(blank = unlimited)</span>
            <input type="number" min={0} value={children} onChange={(e) => setChildren(e.target.value)} />
          </label>
        </div>
        <label>
          Larger version of <span className="muted small">(a Large Family plan: the plan picker shows it in place of this plan once a family has more children than it covers)</span>
          <select value={largerOf} onChange={(e) => setLargerOf(e.target.value)}>
            <option value="">None</option>
            {data.plans
              .filter((x) => x.id !== plan?.id && !x.largerOf && !x.archived)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
          </select>
        </label>

        <fieldset className="lesson-picker stack">
          <legend>Features</legend>
          <div className="feature-checks">
            {data.features.map((f) => (
              <label key={f.key} className="check" title={f.hint}>
                <input type="checkbox" checked={features.includes(f.key)} onChange={(e) => setFeatures((l) => toggle(l, f.key, e.target.checked))} />
                <span>
                  {f.label}
                  {data.earlyFeatures.includes(f.key) && <span className="muted small"> (early access)</span>}
                </span>
              </label>
            ))}
          </div>
          <label className="check">
            <input type="checkbox" checked={earlyAccess} onChange={(e) => setEarlyAccess(e.target.checked)} />
            <span>
              Early access
              <span className="muted small block">Gets every feature in early access, before other plans.</span>
            </span>
          </label>
        </fieldset>

        <fieldset className="lesson-picker stack">
          <legend>Free period</legend>
          <div className="segmented" role="radiogroup" aria-label="Free period">
            {(
              [
                ['none', 'None'],
                ['trial', 'Trial'],
                ['dates', 'Date range'],
                ['life', 'Free for life'],
                ['ambassador', 'Ambassador access'],
              ] as const
            ).map(([v, l]) => (
              <button key={v} role="radio" aria-checked={free === v} className={free === v ? 'active' : ''} onClick={() => setFree(v)}>
                {l}
              </button>
            ))}
          </div>
          {free === 'trial' && (
            <label>
              Trial length (days, from when the family joins)
              <input type="number" min={1} value={trialDays} onChange={(e) => setTrialDays(e.target.value)} />
            </label>
          )}
          {free === 'dates' && (
            <div className="field-row">
              <label>
                Free from
                <input type="date" value={freeFrom} onChange={(e) => setFreeFrom(e.target.value)} />
              </label>
              <label>
                Free until
                <input type="date" value={freeUntil} onChange={(e) => setFreeUntil(e.target.value)} />
              </label>
            </div>
          )}
          {free === 'life' && <p className="muted small">Families on this plan never pay.</p>}
          {free === 'ambassador' && (
            <p className="muted small">
              For Brand Ambassadors’ own families: no charge while their ambassador is active. When their part ends, the family keeps it for the days set in
              Partners → Program settings, then chooses a plan.
            </p>
          )}
        </fieldset>

        <fieldset className="lesson-picker stack">
          <legend>Sign-up window</legend>
          <p className="muted small">When new families can join this plan. Leave blank for no limit.</p>
          <div className="field-row">
            <label>
              Opens
              <input type="date" value={signupStart} onChange={(e) => setSignupStart(e.target.value)} />
            </label>
            <label>
              Closes
              <input type="date" value={signupEnd} onChange={(e) => setSignupEnd(e.target.value)} />
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            <span>
              Default for new families
              <span className="muted small block">New sign-ups join this plan while its window is open.</span>
            </span>
          </label>
          <label className="check">
            <input type="checkbox" checked={recommended} onChange={(e) => setRecommended(e.target.checked)} />
            <span>
              Recommended on the home page
              <span className="muted small block">Highlighted in the middle of the home page’s plans, with the main sign-up button. Only one plan at a time.</span>
            </span>
          </label>
        </fieldset>

        {data.addons.some((a) => addonOnPlan(a, plan?.id ?? '')) && (
          <fieldset className="lesson-picker stack">
            <legend>Included add-ons</legend>
            <div className="feature-checks">
              {data.addons.filter((a) => addonOnPlan(a, plan?.id ?? '')).map((a) => (
                <label key={a.id} className="check">
                  <input type="checkbox" checked={addonIds.includes(a.id)} onChange={(e) => setAddonIds((l) => toggle(l, a.id, e.target.checked))} />
                  <span>{a.name}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <label className="check">
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
          <span>
            Archived
            <span className="muted small block">Hidden from new sign-ups. Families already on it keep it.</span>
          </span>
        </label>

        <div className="row-between">
          {plan ? (
            <button className="btn small danger-text" onClick={remove}>
              <Trash2 size={14} /> Delete
            </button>
          ) : (
            <span />
          )}
          <button className="btn primary" onClick={save} disabled={busy || !name.trim()}>
            Save plan
          </button>
        </div>
      </div>
    </Sheet>
  );
}

function AddonSheet({
  addon,
  features,
  plans,
  onClose,
  onSaved,
}: {
  addon: Addon | null;
  features: FeatureInfo[];
  plans: Plan[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useParent();
  const [name, setName] = useState(addon?.name ?? '');
  const [kind, setKind] = useState<Addon['kind']>(addon?.kind ?? 'children');
  const [amount, setAmount] = useState(String(addon?.amount ?? 1));
  const [picked, setPicked] = useState<FeatureKey[]>(addon ? addonFeatures(addon) : []);
  const [planIds, setPlanIds] = useState<string[]>(addon?.planIds ?? []);
  const [yearlyPrice, setYearlyPrice] = useState(addon?.priceYearlyCents ? centsToText(addon.priceYearlyCents) : '');
  const [price, setPrice] = useState(centsToText(addon?.priceMonthlyCents ?? 0));
  const [archived, setArchived] = useState(!!addon?.archived);
  // The most a family can buy (#633); blank = no limit.
  const [maxQty, setMaxQty] = useState(addon?.maxQuantity ? String(addon.maxQuantity) : '');

  async function save() {
    const body = {
      name: name.trim(),
      kind,
      maxQuantity: Math.max(0, parseInt(maxQty, 10) || 0),
      amount: Math.max(1, parseInt(amount, 10) || 1),
      features: kind === 'feature' ? picked : [],
      planIds,
      priceYearlyCents: yearlyPrice.trim() ? textToCents(yearlyPrice) : 0,
      priceMonthlyCents: textToCents(price),
      archived,
    };
    try {
      if (addon) await api.put(`/api/admin/addons/${addon.id}`, body);
      else await api.post('/api/admin/addons', body);
      onSaved();
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  async function remove() {
    if (!addon || !confirm(`Delete the ${addon.name} add-on? Families that have it lose it.`)) return;
    try {
      await api.del(`/api/admin/addons/${addon.id}`);
      onSaved();
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  return (
    <Sheet title={addon ? `Edit ${addon.name}` : 'New add-on'} onClose={onClose}>
      <div className="stack plan-form">
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Extra child" />
        </label>
        <div className="field-row">
          <label>
            Adds
            <select value={kind} onChange={(e) => setKind(e.target.value as Addon['kind'])}>
              <option value="children">Children</option>
              <option value="adults">Adults</option>
              <option value="feature">A feature</option>
            </select>
          </label>
          {kind !== 'feature' && (
            <label>
              How many
              <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
          )}
          <label>
            Price per month ($)
            <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0 = free" />
          </label>
          <label>
            Price per year ($)
            <input inputMode="decimal" value={yearlyPrice} onChange={(e) => setYearlyPrice(e.target.value)} placeholder="blank = plan’s discount" />
          </label>
          {kind !== 'feature' && (
            <label>
              Most a family can add
              <input type="number" min={0} max={100} value={maxQty} onChange={(e) => setMaxQty(e.target.value)} placeholder="blank = no limit" />
            </label>
          )}
        </div>
        {kind === 'feature' && (
          <fieldset className="lesson-picker stack">
            <legend>Features it unlocks</legend>
            <div className="feature-checks">
              {features.map((f) => (
                <label key={f.key} className="check">
                  <input type="checkbox" checked={picked.includes(f.key)} onChange={(e) => setPicked((l) => toggle(l, f.key, e.target.checked))} />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {plans.length > 0 && (
          <fieldset className="lesson-picker stack">
            <legend>Plans it’s for</legend>
            <p className="muted small">Tick none to offer it on every plan.</p>
            <div className="feature-checks">
              {plans
                .filter((p) => !p.archived || planIds.includes(p.id))
                .map((p) => (
                  <label key={p.id} className="check">
                    <input type="checkbox" checked={planIds.includes(p.id)} onChange={(e) => setPlanIds((l) => toggle(l, p.id, e.target.checked))} />
                    <span>{p.name}</span>
                  </label>
                ))}
            </div>
          </fieldset>
        )}
        <label className="check">
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
          <span>Archived</span>
        </label>
        <div className="row-between">
          {addon ? (
            <button className="btn small danger-text" onClick={remove}>
              <Trash2 size={14} /> Delete
            </button>
          ) : (
            <span />
          )}
          <button className="btn primary" onClick={save} disabled={!name.trim() || (kind === 'feature' && !picked.length)}>
            Save add-on
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/** Admin → Families: one family's plan and add-ons. */
export function FamilyPlanEditor({ family, onChanged }: { family: AdminFamily; onChanged: () => void }) {
  const { toast } = useParent();
  const [open, setOpen] = useState(false);
  const p: AdminFamilyPlan | undefined = family.plan;
  return (
    <div className="family-plan row-between">
      <span className="small">
        <Crown size={13} className="inline-icon" /> <strong>{p?.planName || 'No plan'}</strong>{' '}
        <span className="muted">· {statusText(p ?? { status: 'unrestricted', freeUntil: '' })}</span>
        {p && p.addons.filter((a) => !a.fromPlan).length > 0 && (
          <span className="muted"> · + {p.addons.filter((a) => !a.fromPlan).map((a) => `${a.name}${a.quantity > 1 ? ` ×${a.quantity}` : ''}`).join(', ')}</span>
        )}
      </span>
      <div className="row-gap">
        {/* Founders (#585): the lifetime discount, marked by hand. */}
        <button
          className={`btn small ${p?.founder ? 'active' : ''}`}
          aria-pressed={!!p?.founder}
          onClick={async () => {
            try {
              const r = await api.put<{ subscriptionUpdated: boolean }>(`/api/admin/families/${family.id}/founder`, { founder: !p?.founder });
              toast({ text: `${family.name} ${p?.founder ? 'is no longer a Founder' : 'is a Founder'}${r.subscriptionUpdated ? '; their subscription was updated' : ''}.`, tone: 'good' });
              onChanged();
            } catch (e) {
              toast({ text: (e as Error).message, tone: 'alert' });
            }
          }}
          title="Founders get the lifetime discount set in Server settings → Payments"
        >
          <Star size={13} className="inline-icon" /> {p?.founder ? 'Founder' : 'Mark Founder'}
        </button>
        <button className="btn small" onClick={() => setOpen(true)}>
          Change plan
        </button>
      </div>
      {open && (
        <FamilyPlanSheet
          family={family}
          onClose={() => setOpen(false)}
          onSaved={() => {
            toast({ text: `${family.name}’s plan saved.`, tone: 'good' });
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function FamilyPlanSheet({ family, onClose, onSaved }: { family: AdminFamily; onClose: () => void; onSaved: () => void }) {
  const { toast } = useParent();
  const [data, setData] = useState<PlansData | null>(null);
  const [planId, setPlanId] = useState(family.plan?.planId ?? '');
  const [qty, setQty] = useState<Record<string, number>>(() =>
    Object.fromEntries((family.plan?.addons ?? []).filter((a) => !a.fromPlan).map((a) => [a.id, a.quantity])),
  );
  useEffect(() => {
    api.get<PlansData>('/api/admin/plans').then(setData).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  async function save() {
    try {
      await api.put(`/api/admin/families/${family.id}/plan`, {
        planId,
        addons: Object.entries(qty)
          .filter(([id, n]) => n > 0 && (!data || data.addons.some((a) => a.id === id && addonOnPlan(a, planId))))
          .map(([id, quantity]) => ({ id, quantity })),
      });
      onSaved();
      onClose();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  return (
    <Sheet title={`${family.name}: plan`} onClose={onClose}>
      {!data ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="stack plan-form">
          <label>
            Plan
            <select value={planId} onChange={(e) => setPlanId(e.target.value)}>
              <option value="">No plan (everything included)</option>
              {data.plans
                .filter((p) => !p.archived || p.id === planId)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.ambassadorAccess ? 'Ambassador access' : p.freeForLife ? 'Free for life' : priceText(p)}
                    {p.trialDays ? ` (${p.trialDays}-day trial)` : ''}
                  </option>
                ))}
            </select>
          </label>
          <p className="muted small">Changing the plan starts a new trial if the plan has one.</p>
          {data.addons.filter((a) => !a.archived && addonOnPlan(a, planId)).length > 0 && (
            <fieldset className="lesson-picker stack">
              <legend>Add-ons for this family</legend>
              {data.addons
                .filter((a) => !a.archived && addonOnPlan(a, planId))
                .map((a) => (
                  <label key={a.id} className="addon-row">
                    <span>
                      {a.name} <span className="muted small">{a.kind === 'feature' ? '' : `(+${a.amount} ${a.kind} each)`}</span>
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={a.kind === 'feature' ? 1 : 100}
                      value={qty[a.id] ?? 0}
                      onChange={(e) => setQty((q) => ({ ...q, [a.id]: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                      aria-label={`${a.name} quantity`}
                    />
                  </label>
                ))}
            </fieldset>
          )}
          <button className="btn primary" onClick={save}>
            Save
          </button>
        </div>
      )}
    </Sheet>
  );
}
